import merge from 'lodash/merge';

import config from '../../config';
import fetch, { toQueryString } from '../fetch';
import RestEntityAPI from './restEntityAPI';

const options = {
  entityName: 'subOrganizations',
  endpoint: organizationId => `${config.apiUrl}/v1/orgs/${organizationId}/sub-orgs`,
  searchEndpoint: organizationId => `${config.apiUrl}/v1/orgs/${organizationId}/sub-orgs`, // TODO change this when documentation available
  orgEndpoint: `${config.apiUrl}/v1/orgs`,
};

const SubOrganizationsAPI = () => {
  const parentAPI = RestEntityAPI(options);

  const createOrganization = async (data) => {
    try {
      const response = await fetch(options.orgEndpoint, { method: 'POST', body: { data } });
      return response?.data;
    } catch (error) {
      throw error;
    }
  };

  const updateOrganization = async (orgId, data) => {
    const url = `${options.orgEndpoint}/${orgId}`;
    try {
      const response = await fetch(url, { method: 'PATCH', body: { data } });
      return response?.data;
    } catch (error) {
      throw error;
    }
  };

  const getConversionPixel = async (orgId, subOrgId) => {
    const url = `${options.orgEndpoint}/${orgId}/conversion-pixel${toQueryString({
      sub_organization_id: subOrgId,
    })}`;
    try {
      const response = await fetch(url, { method: 'GET' });
      return response?.data;
    } catch (error) {
      throw error;
    }
  };

  const createSubOrganization = async (data, orgId) => {
    try {
      const response = await fetch(`${parentAPI.buildEndpoint(options.endpoint, orgId)}`, {
        method: 'POST',
        body: data,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const updateSubOrganization = async (data, orgId, subOrgId) => {
    try {
      const response = await fetch(`${parentAPI.buildEndpoint(options.endpoint, orgId)}/${subOrgId}`, { method: 'PATCH', body: data });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getAllSubOrgs = async (organizationId, query = {}, profileKey) => {
    const { name, ...restQuery } = query;
    const isNameEmpty = !(name?.length);
    const getUrl = `${options.endpoint(organizationId)}${toQueryString(isNameEmpty ? restQuery : query)}`;
    const headers = (profileKey != null) ? { 'X-Profile-Key': profileKey, 'X-Check-Roles': true } : {};
    try {
      return await fetch(getUrl, { method: 'GET', headers });
    } catch (error) {
      throw error;
    }
  };

  const getSubOrganization = async ({ orgId, subOrgId, ctx }, profileKey) => {
    const getUrl = `${parentAPI.buildEndpoint(options.endpoint, orgId)}/${subOrgId}`;
    const headers = (profileKey != null) ? { 'X-Profile-Key': profileKey } : {};
    try {
      return await fetch(getUrl, {
        method: 'GET',
        ctx: ctx || {},
        headers,
      });
    } catch (error) {
      throw error;
    }
  };

  const getOrganization = async ({ organizationId, ctx }, profileKey) => {
    const getUrl = `${options.orgEndpoint}/${organizationId}`;
    const headers = (profileKey != null) ? { 'X-Profile-Key': profileKey } : {};
    try {
      return await fetch(getUrl, { method: 'GET', ctx: ctx || {}, headers });
    } catch (error) {
      throw error;
    }
  };

  const getOrganizationAdminDetails = async ({ organizationId, ctx }) => {
    const baseUrl = `${options.orgEndpoint}/${organizationId}/admin`;
    try {
      const response = await fetch(baseUrl, {
        method: 'GET',
        ctx: ctx || {},
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const getOrganizationProfiles = async (organizationId, query) => {
    const baseUrl = `${options.orgEndpoint}/${organizationId}/profiles${toQueryString(query)}`;
    try {
      const response = await fetch(baseUrl, {
        method: 'GET',
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const editOrganizationAdminDetails = async (organizationId, data) => {
    const baseUrl = `${options.orgEndpoint}/${organizationId}/admin`;
    try {
      const response = await fetch(baseUrl, {
        method: 'PATCH',
        body: {
          data,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const editOrganizationDetails = async (organizationId, options) => {
    const baseUrl = `${config.apiUrl}/v1/orgs/${organizationId}`;
    try {
      const response = await fetch(baseUrl, {
        method: 'PATCH',
        body: {
          data: options,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const getOrganizationAssetsDetails = async (organizationId, query) => {
    const baseUrl = `${options.orgEndpoint}/${organizationId}/assets${toQueryString(query)}`;
    try {
      const response = await fetch(baseUrl, { method: 'GET' });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const getSubOrganizationAssets = async (organizationId, subOrganizationId, query) => {
    const baseUrl = `${options.orgEndpoint}/${organizationId}/sub-orgs/${subOrganizationId}/assets${toQueryString(query)}`;
    try {
      const response = await fetch(baseUrl, { method: 'GET' });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const createOrganizationAssets = async (organizationId, data) => {
    const baseUrl = `${options.orgEndpoint}/${organizationId}/assets`;
    try {
      const response = await fetch(baseUrl, { method: 'POST', body: data });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const createSubOrganizationAssets = async (organizationId, subOrganizationId, data) => {
    const baseUrl = `${options.orgEndpoint}/${organizationId}/sub-orgs/${subOrganizationId}/assets`;
    try {
      const response = await fetch(baseUrl, { method: 'POST', body: data });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const editOrganizationAssets = async (organizationId, assetId, data) => {
    const baseUrl = `${options.orgEndpoint}/${organizationId}/assets/${assetId}`;
    try {
      const response = await fetch(baseUrl, { method: 'PATCH', body: data });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const editSubOrganizationAssets = async (organizationId, subOrganizationId, assetId, data) => {
    const baseUrl = `${options.orgEndpoint}/${organizationId}/sub-orgs/${subOrganizationId}/assets/${assetId}`;
    try {
      const response = await fetch(baseUrl, { method: 'PATCH', body: data });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const createOrganizationProfile = async (organizationId, data) => {
    const baseUrl = `${options.orgEndpoint}/${organizationId}/profiles`;
    try {
      const response = await fetch(baseUrl, { method: 'POST', body: data });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const editOrganizationProfile = async (organizationId, data) => {
    const baseUrl = `${options.orgEndpoint}/${organizationId}/profiles`;
    try {
      const response = await fetch(baseUrl, { method: 'PATCH', body: data });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const deleteOrganizationAssets = async (organizationId, assetId) => {
    const baseUrl = `${options.orgEndpoint}/${organizationId}/assets/${assetId}`;
    try {
      const response = await fetch(baseUrl, { method: 'DELETE' });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const deleteSubOrganizationAssets = async (organizationId, subOrganizationId, assetId) => {
    const baseUrl = `${options.orgEndpoint}/${organizationId}/sub-orgs/${subOrganizationId}/assets/${assetId}`;
    try {
      const response = await fetch(baseUrl, { method: 'DELETE' });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const deleteSubOrganization = async (organizationId, subOrgId) => {
    const baseUrl = `${options.orgEndpoint}/${organizationId}/sub-orgs/${subOrgId}`;
    try {
      const response = await fetch(baseUrl, { method: 'DELETE' });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const getOrganizationAdminDocument = async ({ organizationId, ctx }) => {
    const baseUrl = `${options.orgEndpoint}/${organizationId}/documentation`;
    try {
      const response = await fetch(baseUrl, {
        method: 'GET',
        ctx: ctx || {},
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const addOrganizationDocument = async ({ organizationId, data }) => {
    const baseUrl = `${options.orgEndpoint}/${organizationId}/documentation`;
    try {
      const response = await fetch(baseUrl, {
        method: 'POST',
        body: data,
        formData: true,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const downloadOrganizationAdminDocument = async ({ organizationId, disposition, data, ctx }) => {
    const baseUrl = `${options.orgEndpoint}/${organizationId}/documentation/download?disposition=${disposition}`;
    try {
      const response = await fetch(baseUrl, {
        method: 'POST',
        body: {
          data: data[0],
        },
        fullResponse: true,
        ctx: ctx || {},
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getOrgProfiles = async (organizationId, query, ctx) => {
    try {
      return await fetch(`${options.orgEndpoint}/${organizationId}/profiles${toQueryString(query)}`, {
        method: 'GET',
        ctx: ctx || {},
      });
    } catch (error) {
      throw error;
    }
  };

  const getSubOrgsCandidatesCount = async (organizationId, ctx) => {
    const query = { context: 'SUB_ORGANIZATION' };
    try {
      return await fetch(`${options.orgEndpoint}/${organizationId}/candidates/counts${toQueryString(query)}`, {
        method: 'GET',
        ctx: ctx || {},
      });
    } catch (error) {
      throw error;
    }
  };

  return merge({}, { ...parentAPI }, {
    createOrganization,
    createOrganizationAssets,
    createSubOrganization,
    deleteOrganizationAssets,
    deleteSubOrganization,
    editOrganizationAdminDetails,
    editOrganizationAssets,
    editOrganizationDetails,
    getAllSubOrgs,
    getOrganization,
    getOrganizationAdminDetails,
    getOrganizationAssetsDetails,
    getSubOrganization,
    updateSubOrganization,
    getOrganizationAdminDocument,
    downloadOrganizationAdminDocument,
    getOrganizationProfiles,
    createOrganizationProfile,
    editOrganizationProfile,
    getOrgProfiles,
    getSubOrgsCandidatesCount,
    addOrganizationDocument,
    updateOrganization,
    getSubOrganizationAssets,
    createSubOrganizationAssets,
    deleteSubOrganizationAssets,
    editSubOrganizationAssets,
    getConversionPixel,
  });
};

export default SubOrganizationsAPI;
